import { createContext, useMemo, useState, useCallback, useContext, type ReactNode } from 'react';

/** Registered by {@link CapabilityDetailLayout} when overview uses deferred save without a parent patch handler. */
export type OverviewDeferredSaveApi = {
    isDirty: boolean;
    isSaving: boolean;
    save: () => void | Promise<void>;
};

interface CapabilityDetailContextValue {
    /** Open the archive confirmation dialog (agent page uses this for Archive button) */
    openArchiveDialog: () => void;
    /** Latest overview Save controls from layout (null when not applicable or unmounted). */
    overviewDeferredSave: OverviewDeferredSaveApi | null;
    setOverviewDeferredSave: (api: OverviewDeferredSaveApi | null) => void;
    /** Monotonic counter bumped each time a working copy is created via Edit. */
    workingCopyVersion: number;
    /** Increment {@link workingCopyVersion} after creating a working copy. */
    bumpWorkingCopyVersion: () => void;
    /** True when the user is viewing a published (immutable) revision in the version selector. */
    isViewingPublished: boolean;
    setIsViewingPublished: (value: boolean) => void;
    /** Currently selected revision ID in the version selector (synced from layout). */
    selectedRevisionId: string;
    setSelectedRevisionId: (id: string) => void;
    /** Whether the Settings bottom sheet is open. */
    isSettingsOpen: boolean;
    setIsSettingsOpen: (open: boolean) => void;
}

const CapabilityDetailContext = createContext<CapabilityDetailContextValue | null>(null);

export function CapabilityDetailProvider({
    children,
    openArchiveDialog,
}: {
    children: ReactNode;
    openArchiveDialog: () => void;
}) {
    const [overviewDeferredSave, setOverviewDeferredSaveState] = useState<OverviewDeferredSaveApi | null>(null);
    const [workingCopyVersion, setWorkingCopyVersion] = useState(0);
    const [isViewingPublished, setIsViewingPublished] = useState(false);
    const [selectedRevisionId, setSelectedRevisionId] = useState('__working__');
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    const setOverviewDeferredSave = useCallback((next: OverviewDeferredSaveApi | null) => {
        setOverviewDeferredSaveState((prev) => {
            if (next === null) return null;
            if (
                prev &&
                prev.isDirty === next.isDirty &&
                prev.isSaving === next.isSaving &&
                prev.save === next.save
            ) {
                return prev;
            }
            return next;
        });
    }, []);

    const bumpWorkingCopyVersion = useCallback(() => {
        setWorkingCopyVersion((v) => v + 1);
    }, []);

    const value = useMemo(
        () => ({
            openArchiveDialog,
            overviewDeferredSave,
            setOverviewDeferredSave,
            workingCopyVersion,
            bumpWorkingCopyVersion,
            isViewingPublished,
            setIsViewingPublished,
            selectedRevisionId,
            setSelectedRevisionId,
            isSettingsOpen,
            setIsSettingsOpen,
        }),
        [openArchiveDialog, overviewDeferredSave, setOverviewDeferredSave, workingCopyVersion, bumpWorkingCopyVersion, isViewingPublished, selectedRevisionId, isSettingsOpen],
    );

    return <CapabilityDetailContext.Provider value={value}>{children}</CapabilityDetailContext.Provider>;
}

export function useCapabilityDetail() {
    return useContext(CapabilityDetailContext);
}
